import type { EnvResolution } from '@/api/schemas';
import { humanize } from '@/lib/format';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { WarningsList } from '@/components/WarningsList';
import { EnvPill, PlatformIcon, ScopeChip, Tag, VisibilityBadge } from '@/components/badges';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { previewText, previewTitle } from './resolveModel';

type Entry = EnvResolution['entries'][number];
type Missing = EnvResolution['missing'][number];

/** The effective environment: hash, missing requirements, warnings and one row per winning variable. */
export function ResolveResult({ result }: { result: EnvResolution }) {
  return (
    <div className="flex flex-col gap-3" data-testid="env-resolve-result">
      <ResultHeader result={result} />
      {result.missing.length > 0 ? <MissingRequired missing={result.missing} /> : null}
      <WarningsList warnings={result.warnings} />
      <div className="overflow-hidden rounded-lg border">
        <EntriesTable entries={result.entries} />
      </div>
      <Kicker className="text-subtle">Snapshot a build would get: hash {result.contentHash}</Kicker>
    </div>
  );
}

function ResultHeader({ result }: { result: EnvResolution }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <Icon name="fingerprint" size={16} className="text-subtle" />
        content hash <span className="text-foreground">{result.contentHash}</span>
      </span>
      <EnvPill env={result.environment} />
      <PlatformIcon platform={result.platform} withLabel />
      <span className="tabular ml-auto text-subtle">
        {result.entries.length} vars · {result.missing.length} missing
      </span>
    </div>
  );
}

function MissingRequired({ missing }: { missing: Missing[] }) {
  return (
    <div data-testid="env-resolve-missing" className="rounded-lg border border-failed/30 bg-failed/10 px-3 py-2 text-[13px] text-failed">
      <span className="flex items-center gap-1.5 font-medium">
        <Icon name="error" size={16} />
        A build would not start: required variables are missing
      </span>
      <ul className="mt-1.5 flex flex-col gap-1">
        {missing.map((m) => (
          <MissingItem key={m.key} m={m} />
        ))}
      </ul>
    </div>
  );
}

function MissingItem({ m }: { m: Missing }) {
  return (
    <li className="flex flex-wrap items-center gap-2 font-mono text-xs">
      <span className="font-medium">{m.key}</span>
      <Tag className="text-failed">{humanize(m.reason)}</Tag>
      {m.pattern ? <span className="text-failed/80">pattern {m.pattern}</span> : null}
      {m.expectedVisibility ? <span className="text-failed/80">expects {m.expectedVisibility}</span> : null}
      {m.description ? <span className="font-sans text-failed/80">{m.description}</span> : null}
    </li>
  );
}

function EntriesTable({ entries }: { entries: Entry[] }) {
  return (
    <Table aria-label="Effective variables" className="table-fixed">
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="w-[32%]">Key</TableHead>
          <TableHead className="w-[26%]">Wins</TableHead>
          <TableHead className="w-[12%]">Visibility</TableHead>
          <TableHead className="w-[16%]">Preview</TableHead>
          <TableHead>Version id</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((e) => (
          <EntryRow key={e.key} e={e} />
        ))}
      </TableBody>
    </Table>
  );
}

function EntryRow({ e }: { e: Entry }) {
  const preview = previewText(e.visibility, e.preview);
  return (
    <TableRow data-testid={`env-resolve-${e.key}`} className="hover:bg-transparent">
      <TableCell className="py-2">
        <span className="block truncate font-mono text-[13px] text-foreground">{e.key}</span>
        {e.overriddenBy.length > 0 ? <OverriddenBy overriddenBy={e.overriddenBy} /> : null}
      </TableCell>
      <TableCell className="py-2">
        <ScopeChip scope={e.scope} environment={e.environment} platform={e.platform} />
        {e.version ? <Tag className="ml-1">v{e.version}</Tag> : null}
      </TableCell>
      <TableCell className="py-2">
        <VisibilityBadge visibility={e.visibility} />
      </TableCell>
      <TableCell className="truncate py-2 font-mono text-xs text-muted-foreground" title={previewTitle(e.visibility, e.preview)}>
        {preview.masked ? <span className="text-subtle">{preview.text}</span> : preview.text}
      </TableCell>
      <TableCell className="truncate py-2 font-mono text-xs text-subtle">{e.valueFingerprint}</TableCell>
    </TableRow>
  );
}

function OverriddenBy({ overriddenBy }: { overriddenBy: Entry['overriddenBy'] }) {
  return (
    <span className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-subtle">
      over
      {overriddenBy.map((o, i) => (
        <ScopeChip key={i} scope={o.scope} environment={o.environment} platform={o.platform} muted />
      ))}
    </span>
  );
}
